import { ArrowUpDown, Banknote, ChartNoAxesCombined, Gift } from "lucide-react";
import type { NormalizedWidgetFeatures } from "../../features/customization/lib/config-schema";

// All available tabs in priority order
const ALL_TABS = ["Portfolio", "Staking", "DeFi", "Swap"] as const;

export type TabName = (typeof ALL_TABS)[number];

// Icon mapping for each tab
export const TAB_ICONS = {
  Portfolio: ChartNoAxesCombined,
  Staking: Gift,
  DeFi: Banknote,
  Swap: ArrowUpDown,
} as const;

// Route to tab name mapping
export const ROUTE_TO_TAB: Record<string, TabName> = {
  "/": "Portfolio",
  "/staking": "Staking",
  "/defi": "DeFi",
  "/swap": "Swap",
} as const;

// Tab name to route mapping
export const TAB_TO_ROUTE: Record<TabName, string> = {
  Portfolio: "/portfolio/assets",
  Staking: "/staking",
  DeFi: "/defi/portfolio/positions",
  Swap: "/swap",
} as const;

/**
 * Get the first enabled feature/tab
 */
export function getDefaultTab(features: NormalizedWidgetFeatures): TabName {
  return (
    ALL_TABS.find((tab) => {
      const featureKey = tab.toLowerCase() as keyof NormalizedWidgetFeatures;
      return features[featureKey] === true;
    }) || "Portfolio"
  );
}

/**
 * Get enabled tabs based on features
 */
export function getEnabledTabs(features: NormalizedWidgetFeatures): TabName[] {
  return ALL_TABS.filter((tab) => {
    const featureKey = tab.toLowerCase() as keyof NormalizedWidgetFeatures;
    return features[featureKey] === true;
  });
}

/**
 * Get tab to route mapping for only enabled features
 */
export function getEnabledTabRoutes(
  features: NormalizedWidgetFeatures,
): Partial<Record<TabName, string>> {
  return {
    ...(features.portfolio && { Portfolio: TAB_TO_ROUTE.Portfolio }),
    ...(features.staking && { Staking: TAB_TO_ROUTE.Staking }),
    ...(features.defi && { DeFi: TAB_TO_ROUTE.DeFi }),
    ...(features.swap && { Swap: TAB_TO_ROUTE.Swap }),
  };
}

export function createConfigKey(tabData: string[]): string {
  return tabData.join("-");
}

export function getValidActiveTab(activeTab: string, tabData: string[]): string {
  return tabData.includes(activeTab) ? activeTab : tabData[0];
}
